import { mock } from './mock';
import { ErroApi, type Envio, type Mailbox, type Me, type MessageDetail, type MessageList } from './tipos';

export const URL_API = import.meta.env.VITE_INBOX_API_URL ?? '';
export const MODO_MOCK = !URL_API;

async function json<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${URL_API}${path}`, {
    credentials: 'include',
    headers: { Accept: 'application/json', ...(init?.body ? { 'Content-Type': 'application/json' } : {}), ...init?.headers },
    ...init,
  });
  if (r.status === 204) return undefined as T;
  const body = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = body?.error;
    throw new ErroApi(err?.message ?? 'Não deu para completar agora.', r.status, err?.code);
  }
  return body as T;
}

export const api = {
  pedirLink(email: string): Promise<{ token: string | null }> {
    if (MODO_MOCK) return Promise.resolve(mock.pedirLink(email));
    return json('/v1/auth/link', { method: 'POST', body: JSON.stringify({ email }) }).then(() => ({ token: null }));
  },
  trocarToken(token: string): Promise<Me> {
    if (MODO_MOCK) {
      try { return Promise.resolve(mock.trocarToken(token)); }
      catch { return Promise.reject(new ErroApi('token inválido ou expirado', 401)); }
    }
    return json('/v1/auth/session', { method: 'POST', body: JSON.stringify({ token }) });
  },
  eu(): Promise<Me | null> {
    if (MODO_MOCK) return Promise.resolve(mock.eu());
    return json<Me>('/v1/me').catch((e) => {
      if (e instanceof ErroApi && e.status === 401) return null;
      throw e;
    });
  },
  sair(): Promise<void> {
    if (MODO_MOCK) { mock.sair(); return Promise.resolve(); }
    return json('/v1/auth/logout', { method: 'POST' });
  },
  caixas(): Promise<Mailbox[]> {
    if (MODO_MOCK) return Promise.resolve(mock.caixas());
    return json<{ data: Mailbox[] }>('/v1/mailboxes').then((r) => r.data);
  },
  mensagens(id: string, opts?: { q?: string; before?: string }): Promise<MessageList> {
    if (MODO_MOCK) return Promise.resolve(mock.mensagens(id, opts?.q));
    const qs = new URLSearchParams();
    if (opts?.q) qs.set('q', opts.q);
    if (opts?.before) qs.set('before', opts.before);
    const suf = qs.toString() ? `?${qs}` : '';
    return json<MessageList>(`/v1/mailboxes/${id}/messages${suf}`).then((r) => ({
      data: r.data,
      before: r.before ?? null,
    }));
  },
  mensagem(caixa: string, id: string): Promise<MessageDetail> {
    if (MODO_MOCK) {
      const m = mock.mensagem(caixa, id);
      if (!m) return Promise.reject(new ErroApi('não encontrada', 404));
      return Promise.resolve(m);
    }
    return json(`/v1/mailboxes/${caixa}/messages/${id}`);
  },
  patch(caixa: string, id: string, p: { seen?: boolean; archived?: boolean }): Promise<MessageDetail> {
    if (MODO_MOCK) {
      const m = mock.patch(caixa, id, p);
      if (!m) return Promise.reject(new ErroApi('não encontrada', 404));
      return Promise.resolve(m);
    }
    return json(`/v1/mailboxes/${caixa}/messages/${id}`, { method: 'PATCH', body: JSON.stringify(p) });
  },
  enviar(caixa: string, body: Envio): Promise<MessageDetail> {
    if (MODO_MOCK) return Promise.resolve(mock.enviar(caixa, body));
    return json(`/v1/mailboxes/${caixa}/messages`, { method: 'POST', body: JSON.stringify(body) });
  },
};
